/** @jsxImportSource @emotion/react */
import React, { memo, useContext, useMemo } from 'react'
import _isFunction from 'lodash/isFunction'
import { useLocation } from 'react-router-dom'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import TabsNav from './TabsNav'
import UserMenu, { UserMenuProps } from './UserMenu'
import {
  HeaderContainer,
  HeaderContent,
  LeftSection,
  RightSection,
  BrandSection,
  TitleSection,
  HeaderBrand,
  AppTitle,
  ParentAppTitle
} from './styles'
import {
  MenuServiceFetchContextType,
  MenuServiceItemsType,
  MenuServiceSubItemType
} from '@signal/core/menuService/types'
import {
  filterServiceMenuItemsForPopover,
  findActiveServiceMenuRootItemIndex,
  findBestPrefixMatch
} from '@signal/core/menuService/utils'
import isArray from 'lodash/isArray'
import isUndefined from 'lodash/isUndefined'
import { stripDashesFromAnyGuids } from '@signal/utils/stripDashesFromGuid'
import stripQueryParams from '@signal/utils/stripQueryParams'
import MenuServiceContext from '@signal/core/menuService/Context'
import renderSwitch from '@signal/utils/renderSwitch'
import TypographySkeleton from '../Skeleton/Typography'

export interface HeaderNewProps {
  children?: React.ReactNode
  title?: string | ((props: Record<string, unknown>) => string)
  accountNameOverride?: string
  onLogout?: () => void
  onAccountChange?: ({ accountId }: { accountId: string }) => void
  showFontSlider?: boolean
  UserMenuNavLinks?: React.ComponentType<Record<string, unknown>>
  hideRightHandMenu?: boolean
  hideLogo?: boolean
  onNavigate?: (link: string) => void
  baseUrl?: string
  isSkeleton?: boolean
  [key: string]: unknown // For additional props
}

function getUserMenu(props: Partial<UserMenuProps> & Record<string, unknown>) {
  return () => <UserMenu defaultOpen={false} {...(props as UserMenuProps)} />
}

export function HeaderNew({
  children,
  title,
  accountNameOverride,
  onLogout,
  onAccountChange,
  showFontSlider,
  UserMenuNavLinks,
  hideRightHandMenu = false,
  hideLogo = true,
  baseUrl,
  onNavigate,
  isSkeleton,
  ...headerProps
}: HeaderNewProps): JSX.Element {
  const location = useLocation()

  // remove the domain from the baseUrl so we are just left with the path
  const normalizedBasePath = useMemo(() => {
    if (!baseUrl) return undefined
    try {
      return stripQueryParams(new URL(baseUrl as string).pathname)
    } catch {
      console.warn(
        'baseUrl is not a valid absolute URL, baseUrl prop should be an absolute URL. Header tabs may not work as expected.'
      )
      // baseUrl is not a valid absolute URL, return undefined
      return undefined
    }
  }, [baseUrl])

  const { response: serviceMenu = [] } =
    useContext<MenuServiceFetchContextType>(MenuServiceContext)

  const { activeValue, navMenu, tabsMenu } = useMemo(() => {
    const navMenu = isArray(serviceMenu)
      ? filterServiceMenuItemsForPopover(serviceMenu as MenuServiceItemsType)
      : []
    const activeValue = location
      ? findActiveServiceMenuRootItemIndex(
          isArray(serviceMenu) ? serviceMenu : [],
          location.pathname
        )
      : undefined
    const subMenu: Array<MenuServiceSubItemType> =
      (navMenu &&
        !isUndefined(activeValue) &&
        navMenu[activeValue].subMenuItems) ||
      []

    // look for an exact match first, then take anything that starts with the path
    const normalizedPathname = location
      ? stripDashesFromAnyGuids(location.pathname).replace(
          normalizedBasePath ?? '',
          ''
        )
      : ''

    const activeSubMenuItem =
      subMenu.find(
        ({ link }: MenuServiceSubItemType) =>
          normalizedPathname === stripDashesFromAnyGuids(stripQueryParams(link))
      ) ||
      (findBestPrefixMatch(subMenu, normalizedPathname) as
        | MenuServiceSubItemType
        | undefined)

    const tabsMenu = activeSubMenuItem?.subMenuItems ?? []

    return {
      activeValue,
      navMenu,
      tabsMenu
    }
  }, [location, serviceMenu, normalizedBasePath])

  const parentAppTitle =
    (activeValue || activeValue === 0) && navMenu[activeValue]
      ? navMenu[activeValue].name
      : ''

  const titleText = _isFunction(title) ? title(headerProps) : title

  const RightHandMenu = useMemo(
    () =>
      getUserMenu({
        ...headerProps,
        accountNameOverride,
        onAccountChange,
        onLogout,
        showFontSlider,
        UserMenuNavLinks,
        isSkeleton
      }),
    [
      accountNameOverride,
      headerProps,
      onAccountChange,
      onLogout,
      showFontSlider,
      UserMenuNavLinks,
      isSkeleton
    ]
  )

  return (
    <HeaderContainer id="appHeader">
      <HeaderContent>
        <LeftSection $hasLogo={!hideLogo}>
          {!hideLogo && (
            <BrandSection>
              <HeaderBrand height={75} />
            </BrandSection>
          )}
          <TitleSection>
            {renderSwitch([
              {
                condition: Boolean(titleText) && !isSkeleton,
                component: () => (
                  <>
                    {parentAppTitle && (
                      <ParentAppTitle>{parentAppTitle}</ParentAppTitle>
                    )}
                    <AppTitle>{titleText}</AppTitle>
                  </>
                )
              },
              {
                condition: isSkeleton === true && isSkeleton !== undefined,
                component: () => (
                  <>
                    {/* @ts-ignore */}
                    <TypographySkeleton height={24} width={150} />
                    {/* @ts-ignore */}
                    <TypographySkeleton height={24} width={100} />
                  </>
                )
              }
            ])}
          </TitleSection>
        </LeftSection>
        {children}
        <TabsNav
          tabs={tabsMenu}
          currentPathname={location?.pathname}
          onNavigate={onNavigate}
          basePath={normalizedBasePath}
        />
        <RightSection>
          {!hideRightHandMenu && RightHandMenu && <RightHandMenu />}
        </RightSection>
      </HeaderContent>
    </HeaderContainer>
  )
}

export default memo(withCommonSnapshotsOnly(HeaderNew))
